<template>

	<!-- Modalita' inline: solo il calendario, senza menu ne' campo di testo -->
	<v-date-picker v-if="inline"
		:value="value"
		:type="type === 'month' ? 'month' : 'date'"
		:disabled="disabled"
		:min="min"
		:max="max"
		color="primary"
		locale="it"
		:first-day-of-week="1"
		v-bind="pickerProps"
		@input="onPick"
		@update:picker-date="$emit('update:picker-date', $event)"
	></v-date-picker>

	<v-menu v-else
		v-model="menu"
		:close-on-content-click="false"
		transition="scale-transition"
		offset-y
		offset-overflow
		:max-width="maxWidth"
		:min-width="minWidth"
		:nudge-right="nudgeRight"
		style="width: 100%"
		:disabled="disabled"
		v-bind="menuProps"
		>
		<template v-slot:activator="{ on, attrs }">
			<slot name="activator" v-bind="{ on, attrs, formatted: formattedValue }">
				<!-- due rami identici salvo v-mask: la direttiva non si puo' applicare in modo
					 condizionale, e vue-the-mask va in errore se il valore non e' una stringa -->
				<v-text-field v-if="editable"
					v-bind="Object.assign({}, attrs, fieldAttrs)"
					v-on="Object.assign({}, on, fieldListeners)"
					v-mask="mask"
					v-validate="validation"
				></v-text-field>
				<v-text-field v-else
					v-bind="Object.assign({}, attrs, fieldAttrs)"
					v-on="Object.assign({}, on, fieldListeners)"
					v-validate="validation"
				></v-text-field>
			</slot>
		</template>

		<!-- datetime: wizard a due step, prima il giorno poi l'ora -->
		<v-date-picker v-if="!isDatetime || step === 0"
			:value="isDatetime ? datePart : value"
			:type="type === 'month' ? 'month' : 'date'"
			:picker-date.sync="pickerDate"
			:disabled="disabled"
			:min="min"
			:max="max"
			color="primary"
			locale="it"
			scrollable
			:first-day-of-week="1"
			v-bind="pickerProps"
			@input="onPick"
			>
			<template v-if="showToday">
				<v-spacer></v-spacer>
				<v-btn
					text
					small
					color="primary"
					:disabled="disabled || !isTodaySelectable"
					@click="setToday"
				>
					<v-icon left small>mdi-calendar-today</v-icon>
					Oggi
				</v-btn>
			</template>
		</v-date-picker>

		<v-time-picker v-if="isDatetime && step === 1"
			:value="timePart"
			:disabled="disabled"
			:allowed-minutes="allowedMinutes"
			color="primary"
			format="24hr"
			@input="onTimeInput"
			@click:minute="onTimeConfirmed"
		></v-time-picker>
	</v-menu>

</template>

<script>

	export default {

		name: 'GMHDatePicker',

		// Il campo va registrato nel validator del componente CHIAMANTE, altrimenti il suo
		// errors.collect(<vvName>) resta vuoto e validateAll() non vede la regola.
		// NON usare inject: ['$validator'] -> vee-validate installa un validator su ogni
		// componente, Vuetify inclusa, quindi vincerebbe la v-card piu' vicina e le regole
		// finirebbero in un bag che nessuno controlla (required sempre passato, in silenzio).
		// Chi usa la validazione passa quindi :validator="$validator".
		// NB: le regole custom date_min/date_max validano la stringa DD/MM/YYYY, non l'ISO,
		// percio' v-validate deve restare sul v-text-field interno e non sul componente.
		created() {
			if(this.validator) this.$validator = this.validator
		},

		props: {
			validator: { type: Object, default: null },

			value: { type: String, default: null },
			// 'date' -> YYYY-MM-DD, 'month' -> YYYY-MM
			type: { type: String, default: 'date' },
			min: { type: String, default: undefined },
			max: { type: String, default: undefined },

			label: { type: String, default: undefined },
			hint: { type: String, default: undefined },
			persistentHint: { type: Boolean, default: false },
			suffix: { type: String, default: undefined },
			placeholder: { type: String, default: undefined },
			disabled: { type: Boolean, default: false },
			clearable: { type: Boolean, default: true },
			outlined: { type: Boolean, default: true },
			dense: { type: Boolean, default: true },
			hideDetails: { type: Boolean, default: false },
			prependIcon: { type: String, default: undefined },
			appendIcon: { type: String, default: 'event' },
			fieldClass: { type: [String, Object, Array], default: undefined },

			// solo per type="datetime": passi consentiti nei minuti (es. m => m % 5 === 0)
			allowedMinutes: { type: Function, default: undefined },

			// regole Vuetify (array di funzioni), alternative a vee-validate
			rules: { type: Array, default: undefined },
			required: { type: Boolean, default: false },

			validation: { type: String, default: '' },
			vvName: { type: String, default: undefined },
			vvValidateOn: { type: String, default: 'input' },
			vvDelay: { type: [String, Number], default: 0 },
			errorMessages: { type: [String, Array], default: undefined },

			// consente di digitare la data a mano nel campo
			editable: { type: Boolean, default: false },
			showToday: { type: Boolean, default: true },
			inline: { type: Boolean, default: false },
			// alcuni chiamanti si aspettano '' allo svuotamento, altri null
			clearValue: { type: [String, Object], default: '' },

			// Geometria del menu. Sono prop vere e non valori in menuProps perche' Vue non
			// sovrascrive con v-bind le chiavi gia' presenti staticamente sul tag: passarle
			// dentro :menu-props verrebbe ignorato. I default replicano il vecchio pattern
			// copiaincollato, quindi chi non le passa non vede alcun cambiamento.
			// Usare :nudge-right="0" nei contenitori stretti (drawer, dialog, menu annidati).
			nudgeRight: { type: [Number, String], default: 40 },
			minWidth: { type: [Number, String], default: '290px' },
			maxWidth: { type: [Number, String], default: '290px' },

			pickerProps: { type: Object, default: () => ({}) },
			// NB: non puo' sovrascrivere gli attributi gia' impostati sopra sul v-menu
			menuProps: { type: Object, default: () => ({}) }
		},

		data() {
			return {
				menu: false,
				pickerDate: null,
				// solo datetime: 0 = scelta del giorno, 1 = scelta dell'ora
				step: 0,
				// ora scelta nello step 1, prima che venga confermata
				pendingDate: null,
				pendingTime: null
			}
		},

		watch: {
			menu(open) {
				if(!this.isDatetime) return
				if(open) {
					// riaprendo si riparte sempre dal giorno
					this.resetPending()
					return
				}
				// Chiudendo con un giorno gia' scelto va emesso comunque, altrimenti la
				// selezione si perde in silenzio: l'emit avverrebbe solo su @click:minute,
				// che con allowed-minutes non scatta per la maggior parte dei minuti.
				if(this.pendingDate) this.commitDatetime()
			}
		},

		computed: {

			isDatetime() {
				return this.type === 'datetime'
			},

			// parte data/ora del valore corrente, per alimentare i due picker
			datePart() {
				if(this.pendingDate) return this.pendingDate
				return this.value ? String(this.value).substring(0, 10) : null
			},

			// Attenzione: un value di sola data ('YYYY-MM-DD') e' truthy ma substring(11,16)
			// ritorna '', che comporrebbe "2026-07-21 :00" -> moment: "Invalid date".
			timePart() {
				if(this.pendingTime) return this.pendingTime
				let t = this.value ? String(this.value).substring(11, 16) : ''
				return t.length === 5 ? t : '00:00'
			},

			mask() {
				if(this.type === 'month') return '##/####'
				if(this.isDatetime) return '##/##/#### ##:##:##'
				return '##/##/####'
			},

			fieldAttrs() {
				return {
					value: this.formattedValue,
					label: this.label,
					hint: this.hint,
					'persistent-hint': this.persistentHint,
					suffix: this.suffix,
					placeholder: this.placeholder,
					disabled: this.disabled,
					clearable: this.clearable,
					outlined: this.outlined,
					dense: this.dense,
					'hide-details': this.hideDetails,
					'prepend-icon': this.prependIcon,
					'append-icon': this.appendIcon,
					class: this.fieldClass,
					readonly: !this.editable,
					rules: this.rules,
					required: this.required,
					'error-messages': this.errorMessages,
					'data-vv-name': this.vvName || this.label,
					'data-vv-validate-on': this.vvValidateOn,
					'data-vv-delay': this.vvDelay
				}
			},

			fieldListeners() {
				return {
					'click:clear': this.onClear,
					change: this.onTyped
				}
			},

			formattedValue() {
				if(!this.value) return ''
				if(this.type === 'month') return this.$eventBusUtility.formatMonth(this.value)
				if(this.isDatetime) return moment(this.value).format('DD/MM/YYYY HH:mm:ss')
				return this.$eventBusUtility.formatDate(this.value)
			},

			today() {
				// non usare toISOString(): converte in UTC e nelle prime ore del mattino
				// restituisce il giorno precedente
				return moment().format(this.type === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD')
			},

			// min/max possono essere piu' "lunghi" di today (es. today '2026-07' contro
			// min '2026-07-01'): senza troncare, il confronto lessicografico su prefisso
			// direbbe che il mese corrente e' minore del minimo e disabiliterebbe "Oggi".
			isTodaySelectable() {
				let len = this.today.length
				if(this.min && this.today < String(this.min).substring(0, len)) return false
				if(this.max && this.today > String(this.max).substring(0, len)) return false
				return true
			}
		},

		methods: {

			emitValue(val) {
				this.$emit('input', val)
				this.$emit('change', val)
			},

			onPick(val) {
				// datetime: scelto il giorno si passa all'ora, senza chiudere ne' emettere
				if(this.isDatetime) {
					this.pendingDate = val
					this.step = 1
					return
				}
				this.menu = false
				this.emitValue(val)
			},

			onTimeInput(val) {
				this.pendingTime = val
			},

			onTimeConfirmed() {
				this.menu = false
				this.commitDatetime()
			},

			// Compone ed emette il valore datetime. L'ora ha sempre un fallback: senza,
			// un value di sola data produrrebbe "2026-07-21 :00".
			commitDatetime() {
				let date = this.pendingDate || this.datePart
				let time = this.pendingTime || this.timePart || '00:00'
				this.resetPending()
				if(!date) return
				this.emitValue(date + ' ' + time + ':00')
			},

			resetPending() {
				this.step = 0
				this.pendingDate = null
				this.pendingTime = null
			},

			// con type="month" picker-date rappresenta l'anno visualizzato, non il mese
			currentPickerDate() {
				return moment().format(this.type === 'month' ? 'YYYY' : 'YYYY-MM')
			},

			// Chi ha bisogno di fare altro allo svuotamento (es. azzerare campi collegati)
			// ascolta @clear: in quel caso il valore non viene emesso, decide il chiamante.
			onClear() {
				this.pickerDate = this.currentPickerDate()
				this.resetPending()
				if(this.$listeners.clear) {
					this.$emit('clear')
					return
				}
				this.emitValue(this.clearValue)
			},

			setToday() {
				// il calendario resta sul mese del vecchio valore se non si sincronizza picker-date
				this.pickerDate = this.currentPickerDate()
				this.onPick(this.today)
			},

			// solo in modalita' editable: parsing di quanto digitato.
			// Se il testo non e' una data valida non si emette nulla e il valore resta quello
			// precedente (il campo mostra ancora il testo digitato, come faceva il codice
			// originale: il ripristino a video non c'e' mai stato).
			onTyped(text) {
				if(!this.editable) return
				// Svuotare il campo da tastiera vale quanto premere la ✕: senza questo
				// ramo parseDate('') e' falsy, si esce prima dell'emit e il valore
				// precedente resta nel model, che al salvataggio lo riscrive.
				if(!text || !String(text).trim()) {
					this.onClear()
					return
				}
				let parsed
				if(this.type === 'month') parsed = this.parseMonth(text)
				else if(this.isDatetime) parsed = this.parseDatetime(text)
				else parsed = this.$eventBusUtility.parseDate(text)
				if(!parsed) return
				this.menu = false
				this.emitValue(parsed)
			},

			parseMonth(text) {
				if(!text) return null
				let parsed = moment(text, 'MM/YYYY', true)
				return parsed.isValid() ? parsed.format('YYYY-MM') : null
			},

			parseDatetime(text) {
				if(!text) return null
				// l'ora puo' essere omessa o scritta senza secondi
				let parsed = moment(text.replace(/:$/, ''), ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm'])
				return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : null
			}
		}
	}

</script>
